import {useCallback, useRef, useState} from 'react'
import {STEP_MINUTES} from '../helpers/availability'

// Drag-to-paint for the availability grid.
//
// Modelled on Week.tsx's multi-select (components/views/Week.tsx), minus its
// two landmines: that one calls document.querySelectorAll('.rs__cell > button')
// — a document-wide query that would also grab the schedule board's cells —
// and hardcodes a 7-column stride to walk a column. Here the preview lives in
// React state keyed by (day, cell), so there is no DOM query, no stride
// assumption, and the same hook works whatever the column count.
//
// The anchor pins the day: dragging sideways is ignored rather than cancelling,
// so a slightly-off diagonal drag still paints the column the user started in.
// That also guarantees a stroke never spans midnight, which is what keeps
// overnight slots unrepresentable (and matches the server's schema).

export interface PaintPreview {
  dayOfWeek: number
  fromCell: number
  toCell: number
}

interface UseAvailabilityPaintArgs {
  /** Commit a finished stroke. Called once on mouse-up, never mid-drag. */
  onCommit: (dayOfWeek: number, startMinute: number, endMinute: number) => void
  disabled?: boolean
}

export const useAvailabilityPaint = ({onCommit, disabled}: UseAvailabilityPaintArgs) => {
  // The in-flight stroke. State (not a ref) because the cells re-render to show
  // the preview; refs would need a manual repaint like Week.tsx's DOM writes.
  const [preview, setPreview] = useState<PaintPreview | null>(null)
  // Mirrors `preview` for the window-level mouse-up, whose listener closure is
  // registered once and would otherwise capture a stale value.
  const previewRef = useRef<PaintPreview | null>(null)

  const setBoth = useCallback((next: PaintPreview | null) => {
    previewRef.current = next
    setPreview(next)
  }, [])

  const commit = useCallback(() => {
    const current = previewRef.current
    setBoth(null)
    if (!current) return
    const from = Math.min(current.fromCell, current.toCell)
    const to = Math.max(current.fromCell, current.toCell)
    // The cell under the cursor is inclusive, so the range ends at its far edge.
    onCommit(current.dayOfWeek, from * STEP_MINUTES, (to + 1) * STEP_MINUTES)
  }, [onCommit, setBoth])

  const handleMouseDown = useCallback(
    (event: React.MouseEvent, dayOfWeek: number, cellIndex: number) => {
      if (disabled || event.button !== 0) return
      event.preventDefault() // don't start a text selection across the grid
      setBoth({dayOfWeek, fromCell: cellIndex, toCell: cellIndex})

      // Bound to the window, not the cell: releasing outside the grid must
      // still finish the stroke, otherwise the preview sticks and the next
      // click extends a drag the user thought had ended.
      const onWindowMouseUp = () => {
        window.removeEventListener('mouseup', onWindowMouseUp)
        commit()
      }
      window.addEventListener('mouseup', onWindowMouseUp)
    },
    [commit, disabled, setBoth]
  )

  const handleMouseEnter = useCallback(
    (dayOfWeek: number, cellIndex: number) => {
      const current = previewRef.current
      if (!current) return
      // Ignore other columns rather than cancelling — see the note above.
      if (current.dayOfWeek !== dayOfWeek) return
      if (current.toCell === cellIndex) return
      setBoth({...current, toCell: cellIndex})
    },
    [setBoth]
  )

  const isCellInPreview = useCallback(
    (dayOfWeek: number, cellIndex: number) => {
      if (!preview || preview.dayOfWeek !== dayOfWeek) return false
      const from = Math.min(preview.fromCell, preview.toCell)
      const to = Math.max(preview.fromCell, preview.toCell)
      return cellIndex >= from && cellIndex <= to
    },
    [preview]
  )

  return {preview, isCellInPreview, handleMouseDown, handleMouseEnter, isPainting: preview !== null}
}
